import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";

export const pennsylvaniaExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {},
	delays: {},
}).createMachine({
	id: "PENNSYLVANIA_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "INTRO",
					guard: ({ context, event }) => {
						const url = new URL(window.location.href);

						return (
							context.primaryResidenceValue === undefined &&
							url.searchParams.get("q") !== "primaryResidence"
						);
					},
				},
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) =>
						context.currentlyReceivingValue === undefined,
				},
				{
					target: "FARM_LAND",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.is_property_used_for_commercial_agricultural_purposes ===
							undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.specialFieldsValue
								?.is_age_18_or_older_disabled_and_under_45k_per_year,
							context.specialFieldsValue
								?.is_age_65_or_older_and_under_45k_per_year,
							context.specialFieldsValue
								?.is_surving_spouse_of_a_deceased_partner_age_50_or_older,
							context.specialFieldsValue?.is_active_duty_personnel,
							context.noneOfTheAbove,
						].every(value => value === undefined),
				},
				{
					target: "SCHOOL_DISTRICT",
					guard: ({ context, event }) =>
						(commonGuards.currentlyReceivingNoOrNotSure(context) &&
							!context.specialFieldsValue?.municipality) ||
						!context.specialFieldsValue?.school_district,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						[
							context.additionalOwnerFirstNameValue,
							context.additionalOwnerLastNameValue,
						].some(v => v === undefined),
				},
				{
					target: "CO_OP",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.is_co_op_where_some_taxes_are_paid_jointly === undefined,
				},
				{
					target: "PERCENT_OF_CO_OP_OWNERSHIP",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.is_co_op_where_some_taxes_are_paid_jointly === true &&
						context.specialFieldsValue?.percent_of_co_op_ownership ===
							undefined,
				},
				{
					target: "INCOME_PRODUCING",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.is_property_used_as_business_or_rental_property === undefined,
				},
				{
					target:
						"PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.is_property_used_as_business_or_rental_property === true &&
						context.specialFieldsValue
							?.percent_of_square_footage_used_as_business_or_rental_property ===
							undefined,
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							commonGuards.currentlyReceivingNoOrNotSure(context),
						].every(value => value === true),
				},
			],
		},
		INTRO: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				next: {
					target: "PRIMARY_RESIDENCE",
				},
			},
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 13) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
				next: [
					{
						target: "FARM_LAND",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
			},
		},
		FARM_LAND: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "CURRENTLY_RECEIVING",
					actions: assign({
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							is_property_used_for_commercial_agricultural_purposes: undefined,
						}),
					}),
				},
				next: [
					{
						target: "RARE_SITUATIONS",
						guard: ({ context, event }) =>
							context.specialFieldsValue
								?.is_property_used_for_commercial_agricultural_purposes ===
							false,
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "FARM_LAND",
						actions: assign({
							noneOfTheAbove: context => undefined,
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_age_18_or_older_disabled_and_under_45k_per_year: undefined,
								is_age_65_or_older_and_under_45k_per_year: undefined,
								is_surving_spouse_of_a_deceased_partner_age_50_or_older:
									undefined,
								is_active_duty_personnel: undefined,
							}),
						}),
					},
				],
				next: {
					target: "SCHOOL_DISTRICT",
				},
			},
		},
		SCHOOL_DISTRICT: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							municipality: undefined,
							school_district: undefined,
						}),
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "SCHOOL_DISTRICT",
					actions: assign({ ownersValue: context => undefined }),
				},
				next: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "CO_OP",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
					}),
				},
				next: [
					{
						target: "CO_OP",
					},
				],
			},
		},
		CO_OP: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_co_op_where_some_taxes_are_paid_jointly: undefined,
							}),
						}),
					},
					{
						target: "OWNERS",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_co_op_where_some_taxes_are_paid_jointly: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target: "PERCENT_OF_CO_OP_OWNERSHIP",
						guard: ({ context, event }) =>
							context.specialFieldsValue
								?.is_co_op_where_some_taxes_are_paid_jointly === true,
					},
					{
						target: "INCOME_PRODUCING",
					},
				],
			},
		},
		PERCENT_OF_CO_OP_OWNERSHIP: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "CO_OP",
					actions: assign({
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							percent_of_co_op_ownership: undefined,
						}),
					}),
				},
				next: {
					target: "INCOME_PRODUCING",
				},
			},
		},
		INCOME_PRODUCING: {
			entry: [
				assign({
					exemptionsProgress: context => (10 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "PERCENT_OF_CO_OP_OWNERSHIP",
						guard: ({ context, event }) =>
							context.specialFieldsValue
								?.is_co_op_where_some_taxes_are_paid_jointly === true,
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_property_used_as_business_or_rental_property: undefined,
							}),
						}),
					},
					{
						target: "CO_OP",
						actions: assign({
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_property_used_as_business_or_rental_property: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target:
							"PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY",
						guard: ({ context, event }) =>
							context.specialFieldsValue
								?.is_property_used_as_business_or_rental_property === true,
					},
					{
						target: "AGREEMENT",
					},
				],
			},
		},
		PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY: {
			entry: [
				assign({
					exemptionsProgress: context => (11 / 13) * 100,
				}),
			],
			on: {
				back: {
					target: "INCOME_PRODUCING",
					actions: assign({
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							percent_of_square_footage_used_as_business_or_rental_property:
								undefined,
						}),
					}),
				},
				next: {
					target: "AGREEMENT",
				},
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 13) * 100,
				}),
			],
			on: {
				back: [
					{
						target:
							"PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY",
						guard: ({ context, event }) =>
							context.specialFieldsValue
								?.is_property_used_as_business_or_rental_property === true,
					},
					{
						target: "INCOME_PRODUCING",
					},
				],
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
